import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fmtAED } from '../utils/currency';
import {
  Loader2, ChevronRight, Clock, CheckCircle2, XCircle, Wallet,
  Send, PackageCheck, HandCoins, Lock, ArrowLeft, Bot, User as UserIcon,
  Receipt, CheckCircle, Coins, Info, ClipboardList, Building2
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Ordered timeline stages — must match admin platform's request_stages keys 1:1.
// Admin iter-190 added `fees_collected` between `in_progress` and `completed`.
// (rejected is handled separately)
const STAGES = [
  { key: 'pending_review',        icon: Clock,         labelAr: 'قيد المراجعة',                labelEn: 'Pending review' },
  { key: 'approved',              icon: CheckCircle2,  labelAr: 'تمت الموافقة',                 labelEn: 'Approved' },
  { key: 'in_progress',           icon: Send,          labelAr: 'قيد التنفيذ',                  labelEn: 'In progress' },
  { key: 'fees_collected',        icon: Receipt,       labelAr: 'تحصيل الرسوم',                 labelEn: 'Fees collected' },
  { key: 'completed',             icon: PackageCheck,  labelAr: 'تم الإنجاز',                   labelEn: 'Completed' },
  { key: 'commission_collected',  icon: Wallet,        labelAr: 'تحصيل العمولة',                labelEn: 'Commission collected' },
  { key: 'commission_sent',       icon: HandCoins,     labelAr: 'تم إرسال العمولة',              labelEn: 'Commission sent' },
  { key: 'closed',                icon: Lock,          labelAr: 'تم الانتهاء وإغلاق الطلب',       labelEn: 'Completed & Closed' },
];

const STAGE_INDEX = STAGES.reduce((acc, s, i) => { acc[s.key] = i; return acc; }, {});

// Backwards-compat: some older self-submitted docs may still carry the legacy
// `collecting_commission` status. Treat it as an alias of the new
// `commission_collected` key so those rows still render correctly.
const LEGACY_STATUS_ALIASES = {
  collecting_commission: 'commission_collected',
};

function normalizeStatus(s) {
  return LEGACY_STATUS_ALIASES[s] || s;
}

// iter-287 — tiny inline icon so we don't have to import Building2 twice
function Building2Icon() {
  return <Building2 className="h-3 w-3" />;
}

/**
 * Normalize a service_request from admin into the same shape MyRequests expects.
 * Admin-assigned tasks have `request_stages` (object with done/at/by/locked per key).
 * Self-submitted agent tasks have `status` + `history[]`.
 * This adapter turns admin docs into: { status, history, ... }.
 */
function adaptAdminRequest(doc) {
  const rs = doc.request_stages || {};
  const history = [];
  let currentStage = 'pending_review';
  for (const stage of STAGES) {
    // Also tolerate the legacy admin key name for extra safety.
    const legacyKey = stage.key === 'commission_collected' ? 'collecting_commission' : null;
    const info = rs[stage.key] || (legacyKey ? rs[legacyKey] : null) || {};
    if (info.done) {
      history.push({ status: stage.key, at: info.at || null });
      currentStage = stage.key;
    }
  }
  const isCancelled = !!(doc.is_cancelled || doc.status === 'cancelled');
  const isClosed = !!(rs.closed?.done);
  let effectiveStatus;
  if (isCancelled) effectiveStatus = 'cancelled';
  else if (isClosed) effectiveStatus = 'closed';
  else effectiveStatus = currentStage;
  return {
    id: doc.id,
    request_number: doc.request_no_display || doc.id?.slice(0, 8),
    task_title_ar: doc.service_name_ar || doc.service_name || '',
    task_title_en: doc.service_name_en || doc.service_name || '',
    status: effectiveStatus,
    history,
    // Extras for the details panel
    _source: 'admin_assigned',
    // iter-287 — expose service_type so the UI can split the list into
    // "Manual tasks" vs "Company registrations". Falls back to
    // 'manual_task' for legacy admin docs that predate the taxonomy.
    _service_type: doc.service_type || 'manual_task',
    _manual_entry: doc.manual_entry ?? null,
    _priority: doc.priority || null,
    _review_status: doc.review_status || null,
    _customer_phone: doc.phone || null,
    _service_name_ar: doc.service_name_ar || null,
    _service_name_en: doc.service_name_en || null,
    _fees_collected: !!(rs.fees_collected?.done),
    // iter-287 — client info (the end customer whose file this SR is for).
    // Only surfaced when service_type === 'company_registration'.
    _client_name: doc.client_name || null,
    _client_email: doc.client_email || null,
    _client_phone: doc.client_phone || null,
    _company_registration_id: doc.company_registration_id || null,
    // iter-190b admin-visible amounts (mirrored from admin ledger on stage activation)
    paid_total_agent_visible: doc.paid_total_agent_visible ?? null,
    total_price: doc.total_price ?? null,
    fees_collected_at: doc.fees_collected_at || null,
    commission_percent_agent_visible: doc.commission_percent_agent_visible ?? null,
    commission_amount_agent_visible: doc.commission_amount_agent_visible ?? null,
    commission_base_agent_visible: doc.commission_base_agent_visible ?? null,
    commission_scope_agent_visible: doc.commission_scope_agent_visible ?? null,
    commission_collected_at: doc.commission_collected_at || null,
    // iter-195: cancellation (admin-initiated)
    is_cancelled: isCancelled,
    cancelled_at: doc.cancelled_at || null,
    cancelled_by: doc.cancelled_by || null,
    cancel_reason_ar: doc.cancel_reason_ar || null,
    cancel_reason_en: doc.cancel_reason_en || null,
    status_history: doc.status_history || [],
    // iter-202: request_date is the admin-editable business date; created_at
    // is auto-synced to it. submitted_at is audit-only — never displayed.
    created_at: doc.request_date || doc.created_at || null,
    submitted_at: doc.request_date || doc.created_at || null,
    mode: 'admin',
  };
}

function StatusPill({ status, language, isDarkMode }) {
  const normalized = normalizeStatus(status);
  const s = STAGES.find(x => x.key === normalized);
  if (status === 'rejected') {
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
        isDarkMode ? 'bg-red-500/15 text-red-400 border border-red-500/30' : 'bg-red-50 text-red-700 border border-red-200'
      }`}>
        <XCircle className="h-3.5 w-3.5" />
        {language === 'ar' ? 'مرفوض' : 'Rejected'}
      </span>
    );
  }
  if (status === 'cancelled') {
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
        isDarkMode ? 'bg-red-500/15 text-red-300 border border-red-500/40' : 'bg-red-50 text-red-700 border border-red-300'
      }`} data-testid="status-cancelled">
        <XCircle className="h-3.5 w-3.5" />
        {language === 'ar' ? 'ملغى' : 'Cancelled'}
      </span>
    );
  }
  if (!s) return null;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
      isDarkMode ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
    }`}>
      <Icon className="h-3.5 w-3.5" />
      {language === 'ar' ? s.labelAr : s.labelEn}
    </span>
  );
}

function Timeline({ submission, language, isDarkMode }) {
  const isRejected = submission.status === 'rejected';
  const isCancelled = submission.status === 'cancelled' || submission.is_cancelled;
  const status = normalizeStatus(submission.status);
  const currentIdx = (isRejected || isCancelled) ? -1 : (STAGE_INDEX[status] ?? 0);
  const historyByKey = {};
  (submission.history || []).forEach(h => {
    if (h.status) historyByKey[normalizeStatus(h.status)] = h;
  });

  if (isCancelled) {
    const reason = language === 'ar' ? submission.cancel_reason_ar : submission.cancel_reason_en;
    return (
      <div className={`rounded-xl p-4 border ${isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'}`}
           data-testid="timeline-cancelled">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center flex-shrink-0">
            <XCircle className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className={`font-bold text-sm mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-800'}`}>
              {language === 'ar' ? 'تم إلغاء هذا الطلب' : 'This request was cancelled'}
            </h4>
            {reason && (
              <div className={`mt-2 p-3 rounded-lg text-sm ${isDarkMode ? 'bg-red-500/5 text-red-200' : 'bg-white text-red-900'}`}>
                <strong>{language === 'ar' ? 'السبب: ' : 'Reason: '}</strong>
                {reason}
              </div>
            )}
            {submission.cancelled_at && (
              <p className={`text-[11px] mt-2 ${isDarkMode ? 'text-red-300/70' : 'text-red-600'}`}>
                {language === 'ar' ? 'بتاريخ: ' : 'On: '}
                {new Date(submission.cancelled_at).toLocaleString(language === 'ar' ? 'ar-EG' : 'en-US')}
                {submission.cancelled_by && (
                  <> — {language === 'ar' ? 'بواسطة ' : 'by '}{submission.cancelled_by}</>
                )}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isRejected) {
    const stage = submission.reject_stage === 'at_completion'
      ? (language === 'ar' ? 'بعد تنفيذ الطلب' : 'After completion')
      : (language === 'ar' ? 'قبل بدء التنفيذ' : 'Before starting');
    return (
      <div className={`rounded-xl p-4 border ${isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center flex-shrink-0">
            <XCircle className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className={`font-bold text-sm mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-800'}`}>
              {language === 'ar' ? 'تم رفض الطلب' : 'Request Rejected'}
            </h4>
            <p className={`text-xs mb-2 ${isDarkMode ? 'text-red-300/80' : 'text-red-600'}`}>
              {language === 'ar' ? 'مرحلة الرفض:' : 'Rejection stage:'} <strong>{stage}</strong>
            </p>
            {submission.reject_reason && (
              <div className={`mt-2 p-3 rounded-lg text-sm ${isDarkMode ? 'bg-red-500/5 text-red-200' : 'bg-white text-red-900'}`}>
                <strong>{language === 'ar' ? 'السبب: ' : 'Reason: '}</strong>
                {submission.reject_reason}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {STAGES.map((stage, i) => {
        const isDone = i < currentIdx;
        const isActive = i === currentIdx;
        const isFuture = i > currentIdx;
        const StageIcon = stage.icon;
        const stageHistory = historyByKey[stage.key];
        return (
          <div key={stage.key} className="flex gap-3 relative">
            {/* Connector line */}
            {i < STAGES.length - 1 && (
              <div className={`absolute top-9 start-4 w-0.5 h-full -mt-2 ${
                isDone ? 'bg-emerald-500' : (isDarkMode ? 'bg-white/10' : 'bg-gray-200')
              }`} />
            )}
            {/* Icon */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 border-2 ${
              isDone ? 'bg-emerald-500 border-emerald-500 text-white' :
              isActive ? (isDarkMode ? 'bg-emerald-500 border-emerald-500 text-white animate-pulse' : 'bg-emerald-500 border-emerald-500 text-white animate-pulse') :
              (isDarkMode ? 'bg-[#0D1421] border-white/10 text-gray-500' : 'bg-white border-gray-300 text-gray-400')
            }`}>
              <StageIcon className="h-4 w-4" />
            </div>
            {/* Content */}
            <div className={`flex-1 pb-4 ${isFuture ? 'opacity-40' : ''}`}>
              <p className={`text-sm font-semibold ${
                isActive ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-700') :
                (isDarkMode ? 'text-gray-300' : 'text-gray-800')
              }`}>
                {language === 'ar' ? stage.labelAr : stage.labelEn}
              </p>
              {stageHistory?.at && (
                <p className={`text-[11px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {new Date(stageHistory.at).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-US')}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function MyRequests({ embedded = false } = {}) {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('business_token');
    if (!token) { navigate('/login'); return; }
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    // Fetch BOTH agent's own submissions AND admin-assigned manual tasks in parallel.
    // Manual admin tasks come from asb606_admin.service_requests and are visually
    // indistinguishable from self-submissions (per product requirement).
    Promise.all([
      axios.get(`${BACKEND_URL}/api/agent/task-submissions`, auth).then(r => r.data || []).catch(() => []),
      axios.get(`${BACKEND_URL}/api/service-requests`, auth).then(r => (r.data || []).map(adaptAdminRequest)).catch(() => []),
    ]).then(([selfSubs, adminSubs]) => {
      const merged = [...selfSubs, ...adminSubs].sort((a, b) => {
        const at = new Date(a.submitted_at || a.created_at || 0).getTime();
        const bt = new Date(b.submitted_at || b.created_at || 0).getTime();
        return bt - at;
      });
      setSubmissions(merged);
      if (merged.length > 0) setSelected(merged[0]);
    }).finally(() => setLoading(false));
  }, [navigate]);

  // iter-310 — strictly four status filters: all / active / in_progress / closed.
  // No more sub-filter by type; a small inline "Company" badge still
  // distinguishes company_registration rows at a glance.
  const ACTIVE_KEYS = ['pending_review', 'approved'];
  const IN_PROGRESS_KEYS = ['in_progress', 'fees_collected', 'completed', 'commission_collected', 'commission_sent'];
  const CLOSED_KEYS = ['closed', 'cancelled', 'rejected'];

  const filteredSubmissions = submissions.filter((s) => {
    if (statusFilter === 'all') return true;
    const st = normalizeStatus(s.status);
    if (statusFilter === 'active') return ACTIVE_KEYS.includes(st);
    if (statusFilter === 'in_progress') return IN_PROGRESS_KEYS.includes(st);
    if (statusFilter === 'closed') return CLOSED_KEYS.includes(st) || s.is_cancelled;
    return true;
  });

  if (loading) {
    return (
      <div className={`${embedded ? 'py-16' : 'min-h-screen'} flex items-center justify-center cgm-page`}>
        <Loader2 className="h-8 w-8 animate-spin text-[#38BDF8]" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : `min-h-screen cgm-page`}>
      {/* Compact hero — smaller with matching dark tones (hidden when embedded) */}
      {!embedded && (
        <section className={`relative border-b ${isDarkMode ? 'bg-[#0F172A] border-white/5' : 'bg-gradient-to-r from-[#38BDF8]/95 to-[#0EA5E9]/95 border-transparent'}`}>
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
            <button
              onClick={() => navigate('/agent-tasks')}
              className={`inline-flex items-center gap-1.5 text-xs mb-1.5 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-white/85 hover:text-white'}`}
              data-testid="my-requests-back-btn"
            >
              <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" />
              {language === 'ar' ? 'مهام الوكيل' : 'Agent Tasks'}
            </button>
            <div className="flex items-baseline gap-2">
              <h1 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-white'}`} data-testid="my-requests-title">
                {language === 'ar' ? 'طلباتي' : 'My Requests'}
              </h1>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-white/10 text-gray-300' : 'bg-white/25 text-white'}`}>
                {submissions.length}
              </span>
            </div>
          </div>
        </section>
      )}

      <div className={embedded ? '' : 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6'}>
        {/* iter-310 — strictly four status filters, no sub-type chips.
            Order: All → Active → In Progress → Closed */}
        {submissions.length > 0 && (
          <div className={`flex items-center gap-2 mb-4 p-1.5 rounded-xl border overflow-x-auto ${
            isDarkMode ? 'bg-[#131B2C]/70 border-white/10' : 'bg-white/80 border-black/5 shadow-sm'
          }`} data-testid="my-requests-filter">
            {[
              { id: 'all', label: language === 'ar' ? 'الكل' : 'All', count: submissions.length },
              { id: 'active', label: language === 'ar' ? 'فعّال' : 'Active', count: submissions.filter(s => ACTIVE_KEYS.includes(normalizeStatus(s.status))).length },
              { id: 'in_progress', label: language === 'ar' ? 'قيد التنفيذ' : 'In Progress', count: submissions.filter(s => IN_PROGRESS_KEYS.includes(normalizeStatus(s.status))).length },
              { id: 'closed', label: language === 'ar' ? 'تم الإغلاق' : 'Closed', count: submissions.filter(s => CLOSED_KEYS.includes(normalizeStatus(s.status)) || s.is_cancelled).length },
            ].map((chip) => (
              <button
                key={chip.id}
                onClick={() => { setStatusFilter(chip.id); setSelected(null); }}
                data-testid={`filter-chip-${chip.id}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                  statusFilter === chip.id
                    ? 'bg-gradient-to-r from-[#1E9BD9] to-[#0EA5E9] text-white shadow-md'
                    : isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {chip.label}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  statusFilter === chip.id ? 'bg-white/25 text-white' : (isDarkMode ? 'bg-white/10' : 'bg-gray-200')
                }`}>{chip.count}</span>
              </button>
            ))}
          </div>
        )}
        {submissions.length === 0 ? (
          <div className={`rounded-2xl p-10 text-center border ${isDarkMode ? 'bg-[#131B2C] border-white/10' : 'bg-white border-black/5'}`}>
            <PackageCheck className={`h-14 w-14 mx-auto mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              {language === 'ar' ? 'لا توجد طلبات بعد' : 'No requests yet'}
            </p>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {language === 'ar' ? 'ابدأ بتنفيذ مهمة من صفحة مهام الوكيل' : 'Start executing a task from the agent tasks page'}
            </p>
            <button onClick={() => navigate('/agent-tasks')} className="btn-gradient px-5 py-2.5 text-sm inline-flex items-center gap-2">
              {language === 'ar' ? 'استعرض المهام' : 'Browse Tasks'}
              <ChevronRight className="h-4 w-4 rtl:rotate-180" />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* List */}
            <div className="lg:col-span-2 space-y-2">
              {filteredSubmissions.length === 0 && (
                <div className={`text-center py-8 rounded-xl border-2 border-dashed ${isDarkMode ? 'border-white/10 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                  <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{language === 'ar' ? 'لا توجد مهام في هذه الحالة' : 'No tasks in this state'}</p>
                </div>
              )}
              {filteredSubmissions.map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => setSelected(sub)}
                  className={`w-full text-start p-4 rounded-xl border transition-all ${
                    selected?.id === sub.id
                      ? (isDarkMode ? 'bg-[#131B2C] border-[#38BDF8] ring-2 ring-[#38BDF8]/30' : 'bg-white border-[#38BDF8] ring-2 ring-[#38BDF8]/20')
                      : (isDarkMode ? 'bg-[#131B2C] border-white/10 hover:border-white/20' : 'bg-white border-black/5 hover:border-black/10')
                  }`}
                  data-testid={`request-item-${sub.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                        {sub.request_number || sub.id?.slice(0, 8)}
                      </span>
                      {/* iter-287 — Company registration badge distinguishes the row at a glance */}
                      {sub._service_type === 'company_registration' && (
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                        }`} data-testid={`type-badge-company-${sub.id}`}>
                          <Building2Icon />
                          {language === 'ar' ? 'شركة' : 'Company'}
                        </span>
                      )}
                    </div>
                    <StatusPill status={sub.status} language={language} isDarkMode={isDarkMode} />
                  </div>
                  <h3 className={`text-sm font-bold mb-1 line-clamp-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {language === 'ar' ? (sub.task_title_ar || sub.task_id) : (sub.task_title_en || sub.task_title_ar || sub.task_id)}
                  </h3>
                  <div className="flex items-center gap-2 text-xs">
                    {sub.mode === 'admin' ? (
                      <span className={`inline-flex items-center gap-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        <CheckCircle2 className="h-3 w-3" />
                        {language === 'ar' ? 'مُوكَّل' : 'Assigned'}
                      </span>
                    ) : sub.ai_agent_id ? (
                      <span className={`inline-flex items-center gap-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        <Bot className="h-3 w-3" />
                        {language === 'ar' ? 'مساعد ذكي' : 'AI Assist'}
                      </span>
                    ) : (
                      <span className={`inline-flex items-center gap-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        <UserIcon className="h-3 w-3" />
                        {language === 'ar' ? 'يدوي' : 'Manual'}
                      </span>
                    )}
                    <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>·</span>
                    <span className={isDarkMode ? 'text-gray-500' : 'text-gray-500'}>
                      {new Date(sub.created_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US')}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* Detail */}
            <div className="lg:col-span-3">
              {selected && (
                <div className={`rounded-2xl p-5 sm:p-6 border sticky top-4 ${
                  isDarkMode ? 'bg-[#131B2C] border-white/10' : 'bg-white border-black/5'
                }`} data-testid="request-detail">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[11px] font-mono px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                          {selected.request_number || selected.id?.slice(0, 8)}
                        </span>
                      </div>
                      <h2 className={`text-lg font-bold mt-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {language === 'ar' ? (selected.task_title_ar || selected.task_id) : (selected.task_title_en || selected.task_title_ar || selected.task_id)}
                      </h2>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {language === 'ar' ? 'تاريخ التقديم: ' : 'Submitted: '}
                        {new Date(selected.created_at).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-US')}
                      </p>
                    </div>
                    <StatusPill status={selected.status} language={language} isDarkMode={isDarkMode} />
                  </div>

                  {/* iter-195: prominent cancellation banner — shown above all other cards */}
                  {(selected.is_cancelled || selected.status === 'cancelled') && (
                    <div className={`rounded-2xl border-2 p-4 mb-4 ${
                      isDarkMode ? 'border-red-500/40 bg-red-500/10' : 'border-red-300 bg-red-50'
                    }`} data-testid="request-cancelled-banner">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border ${
                          isDarkMode ? 'bg-red-500/20 border-red-500/40' : 'bg-red-100 border-red-300'
                        }`}>
                          <XCircle className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-bold text-base ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>
                            {language === 'ar' ? 'تم إلغاء هذا الطلب' : 'This request was cancelled'}
                          </h3>
                          {(language === 'ar' ? selected.cancel_reason_ar : selected.cancel_reason_en) && (
                            <div className={`text-sm mt-1 ${isDarkMode ? 'text-white/85' : 'text-red-900'}`}>
                              <b>{language === 'ar' ? 'السبب:' : 'Reason:'}</b>{' '}
                              {language === 'ar' ? selected.cancel_reason_ar : selected.cancel_reason_en}
                            </div>
                          )}
                          {selected.cancelled_at && (
                            <div className={`text-[11px] mt-1 ${isDarkMode ? 'text-white/50' : 'text-red-600'}`}>
                              {language === 'ar' ? 'بتاريخ: ' : 'On: '}
                              {new Date(selected.cancelled_at).toLocaleString(language === 'ar' ? 'ar-EG' : 'en-US')}
                              {selected.cancelled_by && (
                                <> — {language === 'ar' ? 'بواسطة ' : 'by '}{selected.cancelled_by}</>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* iter-190b: Fees collected — rich card with paid amount (admin ledger) */}
                  {selected.paid_total_agent_visible != null && (
                    <div className="mb-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-4"
                         data-testid="agent-fees-collected-card">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                        <h3 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-emerald-900'}`}>
                          {language === 'ar' ? 'تم تحصيل الرسوم بالكامل' : 'Fees collected in full'}
                        </h3>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className={`rounded-md px-3 py-2 ${isDarkMode ? 'bg-black/30' : 'bg-white/70'}`}>
                          <div className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>
                            {language === 'ar' ? 'المستحق الإجمالي' : 'Total due'}
                          </div>
                          <div className={`font-bold text-sm mt-0.5 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {fmtAED(selected.total_price || 0)} {language === 'ar' ? 'د.إ' : 'AED'}
                          </div>
                        </div>
                        <div className={`rounded-md px-3 py-2 border ${isDarkMode ? 'bg-emerald-500/10 border-emerald-500/25' : 'bg-emerald-100/70 border-emerald-300'}`}>
                          <div className={isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}>
                            {language === 'ar' ? 'المُحصَّل' : 'Collected'}
                          </div>
                          <div className={`font-bold text-sm mt-0.5 ${isDarkMode ? 'text-emerald-200' : 'text-emerald-800'}`}
                               data-testid="agent-paid-total">
                            {fmtAED(selected.paid_total_agent_visible || 0)} {language === 'ar' ? 'د.إ' : 'AED'}
                          </div>
                        </div>
                      </div>
                      {selected.fees_collected_at && (
                        <div className={`text-[10px] mt-2 ${isDarkMode ? 'text-white/40' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'بتاريخ: ' : 'On: '}
                          {new Date(selected.fees_collected_at).toLocaleString(language === 'ar' ? 'ar-EG' : 'en-US')}
                        </div>
                      )}
                    </div>
                  )}

                  {/* iter-190b: Commission collected — rich card with percent + base + amount */}
                  {selected.commission_amount_agent_visible != null && (
                    <div className="mb-4 rounded-2xl border border-amber-500/25 bg-amber-500/5 p-4"
                         data-testid="agent-commission-card">
                      <div className="flex items-center gap-2 mb-2">
                        <Coins className={`w-4 h-4 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`} />
                        <h3 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-amber-900'}`}>
                          {language === 'ar' ? 'تم تحصيل عمولتك' : 'Your commission collected'}
                        </h3>
                      </div>
                      {/* iter-194: commission scope badge — 'total' (amber) vs 'service_only' (blue, default) */}
                      {(selected.commission_scope_agent_visible || 'service_only') === 'total' ? (
                        <div className={`text-[11px] rounded-md px-3 py-1.5 mb-3 inline-flex items-start gap-1.5 border ${
                          isDarkMode
                            ? 'text-amber-300 bg-amber-500/10 border-amber-500/20'
                            : 'text-amber-800 bg-amber-50 border-amber-200'
                        }`} data-testid="commission-basis-note">
                          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span>
                            {language === 'ar' ? (
                              <>
                                <b>احتساب من الرسم الإجمالي</b> — العمولة أُخذت من الإجمالي (يشمل الرسوم الحكومية).
                                يُستخدم لطلبات ما فيها رسم خدمة مستقل (مثل تأسيس شركة).
                              </>
                            ) : (
                              <>
                                <b>Calculated on total fees</b> — Commission taken from the full total (including government fees).
                                Used for requests without a separate service fee (e.g. company formation).
                              </>
                            )}
                          </span>
                        </div>
                      ) : (
                        <div className={`text-[11px] rounded-md px-3 py-1.5 mb-3 inline-flex items-start gap-1.5 border ${
                          isDarkMode
                            ? 'text-blue-300/90 bg-blue-500/10 border-blue-500/20'
                            : 'text-blue-700 bg-blue-50 border-blue-200'
                        }`} data-testid="commission-basis-note">
                          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span>
                            {language === 'ar' ? (
                              <>
                                <b>احتساب من رسوم الخدمة فقط</b> — العمولة أُخذت من رسم شركتنا فقط.
                                الرسوم الحكومية تبقى بالكامل من نصيبك.
                              </>
                            ) : (
                              <>
                                <b>Calculated on service fees only</b> — Commission taken from our service fee only.
                                Government fees remain fully yours.
                              </>
                            )}
                          </span>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className={`rounded-md px-3 py-2 ${isDarkMode ? 'bg-black/30' : 'bg-white/70'}`}>
                          <div className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>
                            {language === 'ar' ? 'النسبة' : 'Percent'}
                          </div>
                          <div className={`font-bold text-sm mt-0.5 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {selected.commission_percent_agent_visible}%
                          </div>
                        </div>
                        <div className={`rounded-md px-3 py-2 ${isDarkMode ? 'bg-black/30' : 'bg-white/70'}`}>
                          <div className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>
                            {(selected.commission_scope_agent_visible || 'service_only') === 'total'
                              ? (language === 'ar' ? 'الإجمالي' : 'Total')
                              : (language === 'ar' ? 'رسوم الخدمة' : 'Service fees')}
                          </div>
                          <div className={`font-mono text-xs mt-0.5 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {fmtAED(selected.commission_base_agent_visible || 0)} {language === 'ar' ? 'د.إ' : 'AED'}
                          </div>
                        </div>
                        <div className={`rounded-md px-3 py-2 border ${isDarkMode ? 'bg-amber-500/10 border-amber-500/25' : 'bg-amber-100/70 border-amber-300'}`}>
                          <div className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>
                            {language === 'ar' ? 'مبلغ عمولتنا' : 'Our commission'}
                          </div>
                          <div className={`font-bold text-sm mt-0.5 ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}
                               data-testid="agent-commission-amount">
                            {fmtAED(selected.commission_amount_agent_visible || 0)} {language === 'ar' ? 'د.إ' : 'AED'}
                          </div>
                        </div>
                      </div>
                      {selected.commission_collected_at && (
                        <div className={`text-[10px] mt-2 ${isDarkMode ? 'text-white/40' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'بتاريخ: ' : 'On: '}
                          {new Date(selected.commission_collected_at).toLocaleString(language === 'ar' ? 'ar-EG' : 'en-US')}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Legacy: self-submitted agent tasks may carry `commission_amount` via webhook.
                      Only render if the newer admin card above is NOT present. */}
                  {selected.commission_amount_agent_visible == null && selected.commission_amount != null && (
                    <div className={`mb-4 p-3 rounded-lg border flex items-center justify-between ${
                      isDarkMode ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'
                    }`}>
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                        {language === 'ar' ? 'قيمة العمولة' : 'Commission'}
                      </span>
                      <span className={`text-lg font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                        {selected.commission_amount} {language === 'ar' ? 'درهم' : 'AED'}
                      </span>
                    </div>
                  )}

                  {/* Admin review status banner (only for admin-assigned tasks that are still active).
                      iter-223: HIDE when the request has already moved past review — i.e. commission
                      has been collected/sent, or the request is closed/cancelled. The admin platform
                      does not always clear `review_status`, so we gate on effective status here. */}
                  {selected._review_status === 'ready_for_review'
                    && !['closed', 'cancelled', 'rejected', 'commission_collected', 'commission_sent', 'completed']
                        .includes(normalizeStatus(selected.status)) && (
                    <div className={`mb-4 p-3 rounded-lg border flex items-center gap-2 ${
                      isDarkMode ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'
                    }`} data-testid="review-status-audit">
                      <Clock className={`h-4 w-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`} />
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                        {language === 'ar' ? 'قيد التدقيق النهائي' : 'Awaiting final audit'}
                      </span>
                    </div>
                  )}
                  {selected._review_status === 'closed' && (
                    <div className={`mb-4 p-3 rounded-lg border flex items-center gap-2 ${
                      isDarkMode ? 'bg-slate-500/15 border-slate-400/30' : 'bg-slate-100 border-slate-300'
                    }`} data-testid="review-status-closed">
                      <Lock className={`h-4 w-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`} />
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                        {language === 'ar' ? 'مغلق نهائياً' : 'Closed permanently'}
                      </span>
                    </div>
                  )}

                  {/* Fees-collected fallback banner — only when admin didn't send pricing yet */}
                  {selected._fees_collected && selected.paid_total_agent_visible == null && (
                    <div className={`mb-4 p-3 rounded-lg border flex items-center gap-2 ${
                      isDarkMode ? 'bg-emerald-500/10 border-emerald-500/25' : 'bg-emerald-50 border-emerald-200'
                    }`} data-testid="fees-collected-banner">
                      <Receipt className={`h-4 w-4 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`} />
                      <span className={`text-xs font-semibold ${isDarkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
                        {language === 'ar' ? '✓ تم تحصيل كامل الرسوم من العميل' : '✓ Full fees collected from customer'}
                      </span>
                    </div>
                  )}

                  <h3 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {language === 'ar' ? 'مراحل الطلب' : 'Request Timeline'}
                  </h3>
                  <Timeline submission={selected} language={language} isDarkMode={isDarkMode} />

                  {selected.notes && (
                    <div className={`mt-4 p-3 rounded-lg border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                      <p className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {language === 'ar' ? 'ملاحظات' : 'Notes'}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {selected.notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
